import type { PackagingShareWrite } from "./useUpsertPackaging";
import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { productGridKey } from "../products/useGetAllproduct";
import { getProductScoresKey } from "../olapScores/useGetProductScores";
import { packagingCompositesGridKey, packagingElementsGridKey, packagingGridKey } from "./useGetAllPackagings";

/**
 * Wraps org-scoped create and item update for tenant packaging composites.
 *
 * CREATE: `POST /api/organizations/{organizationId}/packaging-composites`
 * UPDATE: `PUT /api/packaging-composites/{id}` (resource-anchored authorization)
 *
 * `PackagingComposite` follows a strict XOR rule (validated server-side):
 *  - **Mode A (frozen)**: `packagingReferenceCompositeId` is set, no
 *    `composition` rows. Inherits scores + SSQ from the sure composite ref.
 *  - **Mode B (custom)**: no reference, at least one `composition` row each
 *    pointing to an existing tenant `Packaging` (any subclass / any mode).
 *    Server walks DFS to refuse cycles.
 *
 * `composition[].child` MUST be a fully-qualified IRI (`/api/packagings/{id}`)
 * to leverage Doctrine STI polymorphism on the base table.
 */
export type PackagingCompositionWrite = {
    "@id"?: IRI;
    id?: number;
    child: IRI;
    amount: number;
    position: number;
    isMain?: boolean;
};

export type PackagingCompositeWrite = {
    id?: number;
    name: string;
    externalId?: string | null;
    weight?: number | null;
    packagingReferenceCompositeId?: number | null;
    expandFromReference?: boolean;
    composition?: PackagingCompositionWrite[];
    shares: PackagingShareWrite[];
    tags?: Record<string, string[]>;
};

type Options = {
    organizationId: number;
    campaignId: number;
    packaging: PackagingCompositeWrite;
};

export const useUpsertPackagingComposite = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: Options) => {
            const { organizationId, campaignId, packaging } = options;
            const response =
                packaging.id === undefined ?
                    await loggedInApiCall.post(`/api/organizations/${organizationId}/packaging-composites`, packaging, {
                        params: { campaign: campaignId },
                    })
                :   await loggedInApiCall.put(`/api/packaging-composites/${packaging.id}`, packaging, {
                        params: { campaign: campaignId, organization_id: organizationId },
                    });

            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([packagingGridKey]);
            queryClient.invalidateQueries([packagingElementsGridKey]);
            queryClient.invalidateQueries([packagingCompositesGridKey]);
            queryClient.invalidateQueries([productGridKey]);
            queryClient.invalidateQueries([getProductScoresKey]);
        },
    });
};
